import ky from 'ky'
import { route } from 'preact-router'
import { env } from '@/constants'
import { Api } from '@/types'

export function hasApiToken(): boolean {
	const token = localStorage.getItem('api_token')
	return token != null && token !== '' && token !== 'null'
}

const base = ky.create({
	prefixUrl: import.meta.env.VITE_API_BASE_URL,
	timeout: env.MODE == 'development' ? 100000 : 10000,
	retry: 1,
	headers: {
		Accept: 'application/json',
	},
	hooks: {
		afterResponse: [
			async (req, opts, res) => {
				switch (res.status) {
					case 422:
						const body = (await res.json()) as Api.Error
						const errors = Object.values(body.errors).flat()
						throw new InvalidArgumentsError(errors)
				}
			},
		],
	},
})

export const auth = base.extend({
	hooks: {
		afterResponse: [
			async (req, opts, res) => {
				switch (res.status) {
					case 401: {
						const onLogin =
							location.pathname.startsWith('/login') ||
							location.pathname.startsWith('/signup')
						if (!onLogin) {
							localStorage.clear()
							route('/login', true)
						}
						throw new UnauthorizedError()
					}
				}
			},
		],
	},
})

export const api = auth.extend({
	hooks: {
		beforeRequest: [
			async req => {
				if (!hasApiToken()) {
					throw new UnauthorizedError()
				}
				req.headers.set('Authorization', `Bearer ${localStorage.getItem('api_token')}`)
			},
		],
	},
})

export const resources = api.extend({
	headers: { 'Cache-Control': 'private, max-age=60' },
})

/**
 * List endpoints may return a plain array or a wrapped payload ({ data, items, ... }).
 * The PWA calls .filter / .find on the parsed body, so always normalize to an array.
 */
export function unwrapListResponse<T>(body: unknown): T[] {
	if (Array.isArray(body)) {
		return body as T[]
	}

	if (body && typeof body === 'object') {
		const record = body as Record<string, unknown>
		for (const key of ['data', 'items', 'reports', 'projects', 'equipment', 'suppliers']) {
			const value = record[key]
			if (Array.isArray(value)) {
				return value as T[]
			}
		}
	}

	return []
}

abstract class ApiError extends Error {}

export class UnauthorizedError extends ApiError {}

export class InvalidArgumentsError extends ApiError {
	constructor(public errors: string[]) {
		super()
	}
}
